import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Lottie from 'react-lottie';
import ArrowDownIcon from '@icons/ArrowDownIcon/ArrowDownIcon';
import GridColumn from '@navi/web-ui/lib/layouts/Grid/GridColumn/GridColumn';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import Typography from '@primitives/Typography/Typography';
import cx from 'classnames';
import { getCashCollectedMtdForCSA, getPerformance } from 'src/pages/auth/AuthActions';
import StarLevelFiveIcon from '../../assets/icons/StarLevelFiveIcon';
import StarLevelFourIcon from '../../assets/icons/StarLevelFourIcon';
import StarLevelOneIcon from '../../assets/icons/StarLevelOneIcon';
import StarLevelSevenIcon from '../../assets/icons/StarLevelSevenIcon';
import StarLevelSixIcon from '../../assets/icons/StarLevelSixIcon';
import StarLevelThreeIcon from '../../assets/icons/StarLevelThreeIcon';
import StarLevelTwoIcon from '../../assets/icons/StarLevelTwoIcon';
import { RootState } from '../../store';
import { DateFormat, getFormatDate, getFormattedDateMonth } from '../../utils/DateHelper';
import { formatAmountIndianFormat } from '../../utils/commonUtils';
import CashEarnedChartHC from './CashEarnedChart/CashEarnedChartHC';
import CumulativeCashCollected from './CumulativeCashCollected';
import styles from './Leaderboard.module.scss';
import PerformanceChart from './PerformanceChart';
import { PERFORMANCE_CHART_TYPE } from './leaderboardConstant';
import { addClickstreamEvent } from '../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../service/clickStream.constant';
import { LevelIcon } from 'src/assets/icons/LevelIcon';
import { levelColorsList } from '../AgentprogressMeter/constant';
import { setIsExpanded } from '@cp/src/reducers/leaderboardSlice';
import { useKeyListener } from '@navi/web-ui/lib/hooks';
import { ESCAPE_KEY } from '@navi/web-ui/lib/constants/Components';
import coinShimmer from '../../assets/json/coin-shimmer.json';
import AnimatedCounter from '../AnimatedCounter/AnimatedCounter';
import AgentProgressMeter from '../AgentprogressMeter/index';
import CsaLeaderboard from './CsaMtdLeaderboard';
import { Roles } from 'src/pages/auth/constants/AuthConstants';

export const LEVEL_ICONS = [
  <StarLevelOneIcon key={1} />,
  <StarLevelTwoIcon key={2} />,
  <StarLevelThreeIcon key={3} />,
  <StarLevelFourIcon key={4} />,
  <StarLevelFiveIcon key={5} />,
  <StarLevelSixIcon key={6} />,
  <StarLevelSevenIcon key={7} />
];
interface LeaderboardProps {
  stickyBanner?: boolean;
}
const Leaderboard: React.FC<LeaderboardProps> = props => {
  const { stickyBanner } = props;
  const dispatch = useDispatch();
  const leaderboard = useSelector((store: RootState) => store?.leaderboard);
  const name = useSelector((store: RootState) => store?.common?.userData?.name);
  const agentReferenceId = useSelector((store: RootState) => store?.common?.userData?.referenceId);
  const performanceData = useSelector((store: RootState) => store?.leaderboard?.monthlyPerformance);
  const isExpanded = useSelector((store: RootState) => store?.leaderboard?.isExpanded);

  const currentDate = new Date();
  const currentDateFormatted = getFormatDate(currentDate, DateFormat.DD_MMM);
  const thirtyDaysPreviousDate = new Date(new Date().setDate(currentDate.getDate() - 29));
  const thirtyDaysNextDate = new Date().setDate(currentDate.getDate() + 29);
  const dateWhenRankingWillBeVisible = new Date(
    leaderboard?.dateWhenRankingVisible || thirtyDaysNextDate
  );

  const dateWhenRankingWillBeVisibleFormatted = getFormatDate(
    dateWhenRankingWillBeVisible,
    DateFormat.DD_MMM_YYYY
  );
  const handleOnClick = () => {
    dispatch(setIsExpanded(!isExpanded));
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_PerfLevel_DropdownClick);
  };

  const get30DaysPreviousDateOrFirstAllocationDate = () => {
    if (!leaderboard.firstAllocationDate) {
      return '';
    }
    const firstAllocationDate = new Date(leaderboard.firstAllocationDate);
    if (firstAllocationDate.getTime() > thirtyDaysPreviousDate.getTime())
      return getFormatDate(firstAllocationDate, DateFormat.DD_MMM);
    return getFormatDate(thirtyDaysPreviousDate, DateFormat.DD_MMM);
  };

  useEffect(() => {
    if (leaderboard.agentLevelRanking && agentReferenceId) {
      dispatch(getPerformance(agentReferenceId, PERFORMANCE_CHART_TYPE.LAST_30_DAYS));
    }
  }, [leaderboard.agentLevelRanking, agentReferenceId]);

  useKeyListener(ESCAPE_KEY, handleOnClick, isExpanded);

  const coinShimmerDefaultOptions = {
    loop: false,
    autoplay: true,
    animationData: coinShimmer,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  const [isCoinShimmerAnimating, setIsCoinShimmerAnimating] = useState(false);
  const isRealTimeCashCollectedForTeleLite = useSelector(
    (store: RootState) => store.common.featureFlags.realTimeCashCollectedForTeleLite
  );

  useEffect(() => {
    setIsCoinShimmerAnimating(true);
  }, [leaderboard?.cashCollectedInLast30Days]);

  const onCoinShimmerAnimationComplete = () => {
    setIsCoinShimmerAnimating(false);
  };

  const roles = useSelector((store: RootState) => store?.common?.userData?.roles);
  const isInhouseCsa = roles?.includes(Roles.ROLE_IN_HOUSE_CSA_AGENT);

  const cashCollectedMtd = useSelector((store: RootState) => store?.common?.cashCollectedMtd);

  const getFirstDateOfCurrentMonth = () => {
    const firstDate = new Date(currentDate);
    firstDate.setDate(1);
    return getFormattedDateMonth(firstDate);
  };

  const getDateRangeText = () => {
    if (currentDate.getDate() === 1) {
      return `(${currentDateFormatted})`;
    } else {
      return `(${getFirstDateOfCurrentMonth()} - ${currentDateFormatted})`;
    }
  };

  return (
    <>
      {leaderboard?.cashCollectedInLast30Days && leaderboard?.maxLevel ? (
        <GridRow
          className={cx(styles.leaderboardContainer, {
            [styles.leaderBoardActive]: isExpanded,
            [styles.stickyBannerPosition]: stickyBanner
          })}
        >
          <GridRow onClick={handleOnClick} className={styles.header}>
            {!isInhouseCsa ? (
              <>
                <Typography variant="p5" className="flex items-center">
                  <span title={name ?? ''} className={cx(styles.ellipsis, 'mr-4')}>
                    {name ? `${name}` : ''}
                  </span>
                </Typography>
                <div className={styles.separator} />
                <div className="w-fit">
                  <div className="flex">
                    <Typography className={cx(styles.mr8)} variant="p5">
                      <span className={styles.headerTextWrapper}>
                        {`Last ${leaderboard?.noOfDaysCashCollected} days cash collected`}
                        <span className={styles.cashCollectedAmount}>
                          <Lottie
                            options={coinShimmerDefaultOptions}
                            height={28}
                            width={28}
                            isStopped={!isCoinShimmerAnimating}
                            eventListeners={[
                              {
                                eventName: 'complete',
                                callback: onCoinShimmerAnimationComplete
                              }
                            ]}
                          />
                          <b style={{ marginRight: 8, marginLeft: 3 }}>
                            <AnimatedCounter
                              {...formatAmountIndianFormat(
                                leaderboard.cashCollectedInLast30Days ?? 0
                              )}
                            />
                          </b>
                        </span>
                        {` (${get30DaysPreviousDateOrFirstAllocationDate()} - ${currentDateFormatted})`}
                      </span>
                    </Typography>
                  </div>
                </div>
                <div className={styles.separator} />
                <div style={{ width: 'fit-content' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography className={cx(styles.mr8)} variant="p5">
                      Perf Level
                    </Typography>
                    {leaderboard?.agentLevelRanking ? (
                      <Typography variant="p5">
                        <LevelIcon
                          level={leaderboard?.agentLevelRanking}
                          color={levelColorsList[leaderboard?.agentLevelRanking - 1]}
                        />
                      </Typography>
                    ) : (
                      <Typography variant="p5">-</Typography>
                    )}
                    <Typography className={styles.outOfColor} variant="p5">
                      / {leaderboard.maxLevel}
                    </Typography>
                    <ArrowDownIcon
                      color="var(--blue-base)"
                      className={cx(isExpanded ? styles['rotate-down'] : styles['rotate'])}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Typography variant="p5" className="flex items-center">
                    <span title={name ?? ''} className={styles.ellipsis}>
                      {name ? `${name}` : ''}
                    </span>
                  </Typography>
                </div>
                <div className={styles.separator} />
                <div>
                  <div className="flex items-center">
                    <Typography variant="p5">
                      <div className={styles.headerTextWrapper}>
                        <span>MTD EMI Cash Collected</span>
                        <span className={styles.cashCollectedAmount}>
                          <Lottie
                            options={coinShimmerDefaultOptions}
                            height={28}
                            width={28}
                            isStopped={!isCoinShimmerAnimating}
                            eventListeners={[
                              {
                                eventName: 'complete',
                                callback: onCoinShimmerAnimationComplete
                              }
                            ]}
                          />
                          <b style={{ marginRight: 8, marginLeft: 3 }}>
                            <AnimatedCounter
                              {...formatAmountIndianFormat(leaderboard?.cashCollectedInLast30Days)}
                            />
                          </b>
                        </span>
                        <span>{getDateRangeText()}</span>
                      </div>
                    </Typography>
                  </div>
                </div>
                <div className={styles.separator} />
                <div style={{ width: 'fit-content' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography className={cx(styles.mr8)} variant="p5">
                      Perf Level
                    </Typography>
                    {leaderboard?.agentLevelRanking ? (
                      <Typography variant="p5">
                        <LevelIcon
                          level={leaderboard?.agentLevelRanking}
                          color={levelColorsList[leaderboard?.agentLevelRanking - 1]}
                        />
                      </Typography>
                    ) : (
                      <Typography variant="p5">-</Typography>
                    )}
                    <Typography className={styles.outOfColor} variant="p5">
                      / {leaderboard.maxLevel}
                    </Typography>
                    <ArrowDownIcon
                      color="var(--blue-base)"
                      className={cx(isExpanded ? styles['rotate-down'] : styles['rotate'])}
                    />
                  </div>
                </div>
              </>
            )}
          </GridRow>
          {isExpanded &&
          leaderboard?.levelConstructList &&
          leaderboard?.levelConstructList?.length > 0 ? (
            <>
              {leaderboard?.agentLevelRanking ? (
                <div
                  className={styles.content}
                  style={{ justifyContent: 'right', flexDirection: 'column', padding: 0 }}
                >
                  <GridRow className="flex justify-center">
                    {performanceData ? (
                      <GridColumn className={styles.left} style={{ alignItems: 'left' }}>
                        {isInhouseCsa ? (
                          <Typography variant="p5" className={styles.graphHeading}>
                            MTD EMI Cash collected
                          </Typography>
                        ) : (
                          <Typography variant="p5" className={styles.graphHeading}>
                            Last {leaderboard?.noOfDaysCashCollected} days Cash Collected
                          </Typography>
                        )}
                        <CumulativeCashCollected data={performanceData} />
                        {isInhouseCsa ? (
                          <Typography
                            className={styles.graphHeading}
                            variant="p5"
                            style={{ marginTop: 24 }}
                          >
                            Performance
                          </Typography>
                        ) : (
                          <Typography
                            className={styles.graphHeading}
                            variant="p5"
                            style={{ marginTop: 24 }}
                          >
                            Last {leaderboard?.noOfDaysCashCollected} days Performance
                          </Typography>
                        )}
                        <PerformanceChart data={performanceData} />
                      </GridColumn>
                    ) : null}
                    {leaderboard?.agentLevelRanking ? (
                      <GridColumn
                        sm={4}
                        className={styles.right}
                        style={{ alignItems: 'center', borderLeft: '1px dashed #CACED5' }}
                      >
                        <Typography className={styles.perfLevel} variant="p3">
                          Performance Levels
                        </Typography>
                        <GridRow
                          style={{
                            padding: '8px 32px 0 0',
                            width: '100%',
                            justifyContent: 'center',
                            marginTop: isRealTimeCashCollectedForTeleLite ? '56%' : '0%'
                          }}
                        >
                          <AgentProgressMeter
                            cashCollected={leaderboard.cashCollectedInLast30Days}
                            currentLevel={leaderboard.agentLevelRanking}
                            levelConstructList={leaderboard.levelConstructList}
                            maxLevel={leaderboard.maxLevel}
                          />
                        </GridRow>
                      </GridColumn>
                    ) : null}
                  </GridRow>
                  {!isInhouseCsa && (
                    <GridColumn className={styles.pieChartWrapper}>
                      <CashEarnedChartHC {...leaderboard} />
                    </GridColumn>
                  )}
                </div>
              ) : (
                <div className={styles.content}>
                  <GridRow
                    style={{
                      margin: 0,
                      width: '100%',
                      justifyContent: 'center'
                    }}
                  >
                    <GridColumn>
                      <Typography variant="p5">Your level will be reflected on</Typography>
                    </GridColumn>
                    <GridColumn>
                      <Typography variant="p5">
                        <strong> {`${dateWhenRankingWillBeVisibleFormatted}`} </strong>
                      </Typography>
                    </GridColumn>
                    <GridColumn>
                      <Typography variant="p5">(after completing 30 days)</Typography>
                    </GridColumn>
                  </GridRow>
                </div>
              )}
            </>
          ) : (
            <></>
          )}
        </GridRow>
      ) : (
        <></>
      )}
      {isExpanded && (
        <div
          onClick={handleOnClick}
          className={cx(styles.overlay, {
            [styles.overlayActive]: isExpanded
          })}
        ></div>
      )}
    </>
  );
};
export default Leaderboard;
